import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { plan } from './tb-auto-lines.js';

/**
 * Keeps the Upgrade Box in the cart in step with the models that come with it.
 *
 * Each model with a custom.upgrade_box metafield gets one box per unit, added as its own line
 * with the `_auto_added` property. The box is removed when the models leave the cart. Boxes the
 * shopper added without the property count towards the total. Once the shopper changes the
 * auto-added line themselves, it is left alone until the models leave the cart.
 * The rules are in tb-auto-lines.js; the discount itself is a Shopify automatic discount.
 *
 * Loaded on every page, so a model added anywhere (quick add, the cart drawer) gets its box.
 * After each cart change the models in the cart come from sections/tb-upgrade-box-config.liquid.
 */

const SOURCE = 'tb-upgrade-box';
const STORAGE_KEY = 'tb-upgrade-box';
const CONFIG_ID = 'tb-upgrade-box-config';

/** @returns {Map<number, {variant: number, available: boolean}>} model product id -> its box */
function readModels(element) {
  const models = new Map();
  for (const entry of JSON.parse(element?.textContent || '[]')) {
    if (entry) models.set(entry[0], { variant: entry[1], available: entry[2] });
  }
  return models;
}

/** The models in the cart as it is now, rendered by the server. */
async function fetchModels() {
  const response = await fetch(`${Theme.routes.cart_url}?section_id=tb-upgrade-box-config`);
  if (!response.ok) throw new Error(`config ${response.status}`);
  const html = await response.text();
  return readModels(new DOMParser().parseFromString(html, 'text/html').getElementById(CONFIG_ID));
}

/**
 * The quantity this script last set per box variant, or 'off' once the shopper took over.
 * Tied to the cart token so a new cart starts clean.
 */
function readState(token) {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return saved?.token === token ? saved.boxes : {};
  } catch {
    return {};
  }
}

function writeState(token, boxes) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, boxes }));
  } catch {}
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url} ${response.status}`);
  return response.json();
}

const getCart = () => fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

/** Tells the drawer, cart page and cart icon that the cart changed. */
function announce(cart, action, lines, source = SOURCE) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { source, items: cart.items, itemCount: cart.item_count, didError: false },
  });
}

/** @param {Map<number, {variant: number, available: boolean}>} [pageModels] the page's own config, fresh at load */
async function sync(pageModels) {
  const [models, cart] = await Promise.all([pageModels ?? fetchModels(), getCart()]);
  const { updates, adds, state: boxes } = plan(cart.items, models, readState(cart.token));
  const lines = [];

  try {
    if (Object.keys(updates).length) {
      await postJson(Theme.routes.cart_update_url, { updates });
      for (const [id, quantity] of Object.entries(updates)) lines.push({ id, quantity });
    }
    if (adds.length) {
      await postJson(Theme.routes.cart_add_url, { items: adds });
      for (const { id, quantity } of adds) lines.push({ id: String(id), quantity });
    }
  } catch {
    // Out of stock or another cart error: the shopper sees nothing and the saved state stays put.
    // shortcut: with two box types, a failed add after a successful update leaves the updated box
    // marked as shopper-changed; fine while every model shares one box.
    if (lines.length) announce(await getCart(), 'update', lines);
    return;
  }

  writeState(cart.token, boxes);
  if (lines.length) announce(await getCart(), 'update', lines);
}

// One sync at a time; changes that arrive meanwhile get one more pass afterwards.
let running = null;
let queued = false;
function scheduleSync(pageModels) {
  if (running) {
    queued = true;
    return;
  }
  running = sync(pageModels)
    .catch(() => {})
    .finally(() => {
      running = null;
      if (queued) {
        queued = false;
        scheduleSync();
      }
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ detail }) => {
      if (detail?.source !== SOURCE && !detail?.didError) scheduleSync();
    })
    .catch(() => {});
});

// "Voeg toe" in sections/tb-upgrade-box-offer.liquid adds the model; the sync then adds the box
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-tb-upgrade-box-add]') : null;
  if (!button || button.disabled) return;
  button.disabled = true;
  try {
    const id = Number(button.dataset.tbUpgradeBoxAdd);
    await postJson(Theme.routes.cart_add_url, { items: [{ id, quantity: 1 }] });
    announce(await getCart(), 'add', [{ id: String(id), quantity: 1 }], 'tb-upgrade-box-offer');
  } catch {
  } finally {
    button.disabled = false;
  }
});

// On load, only when the cart (or this PDP) has something to keep in step
const configElement = document.getElementById(CONFIG_ID);
if (configElement) scheduleSync(readModels(configElement));
